import { Grid2 as Grid, Typography, Box, IconButton } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { Clear } from '@mui/icons-material'
import SelectBox from 'components/SelectBox'
import dayjs from 'dayjs'
import InputBase from 'design-system/input/base'
import SelectWithCheckbox from 'design-system/select/SelectCheckBox'
import ButtonBase from 'design-system/button'
import { useSuperAdmin } from 'components/rules/PermissionByRole'
import { DiagnosticsFiltersProps } from '../../types'
import {
  DIAGNOSTIC_TYPES,
  STATUS_OPTIONS,
  CXRAY_SUBTYPE_OPTIONS,
  CXRAY_REVIEW_OPTIONS,
  CXRAY_PRIORITY_OPTIONS
} from '../../constants'

const DiagnosticsFilters = ({
  filters,
  examType,
  onFilterChange,
  onApplyFilters,
  onClearFilters
}: DiagnosticsFiltersProps) => {
  const isSuperAdmin = useSuperAdmin()

  // O tipo de exame é fixo por tela; o subtipo (Raio-X) só aparece para admins
  // na tela de Raio-X e não altera o comportamento padrão (cxray_pa/cxray_ap).
  const showSubtypeFilter = isSuperAdmin && examType === 'cxray'

  // Filtros de Revisão e Prioridade são exclusivos do Raio-X.
  const showCxrayPriorityFilters = examType === 'cxray'

  return (
    <>
      <Grid size={12}>
        <Typography variant='body1' color='var(--secondary-text-color)'>
          Filtre as análises utilizando os campos abaixo:
        </Typography>
      </Grid>
      <Grid container spacing={2} mt={2}>
        {showSubtypeFilter && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <SelectWithCheckbox
              showSelectAll
              items={CXRAY_SUBTYPE_OPTIONS}
              label='Subtipo (Raio-X)'
              value={filters.subtypes}
              onChange={(_, value) => onFilterChange('subtypes', value)}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SelectWithCheckbox
            showSelectAll
            noneOption={{ label: 'Nenhum resultado', value: 'none' }}
            items={DIAGNOSTIC_TYPES[examType] || []}
            label='Tipos de resultado'
            value={filters.diagnosticTypes}
            onChange={(_, value) => onFilterChange('diagnosticTypes', value)}
          />
        </Grid>
        {showCxrayPriorityFilters && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectWithCheckbox
                items={CXRAY_REVIEW_OPTIONS}
                label='Revisão'
                value={filters.review}
                onChange={(_, value) => onFilterChange('review', value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectWithCheckbox
                items={CXRAY_PRIORITY_OPTIONS}
                label='Prioridade'
                value={filters.priority}
                onChange={(_, value) => onFilterChange('priority', value)}
              />
            </Grid>
          </>
        )}

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InputBase
            placeholder='Identificador da análise'
            label='Identificador da análise'
            value={filters.examId}
            onChange={e => onFilterChange('examId', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InputBase
            label='Identificador do Paciente'
            placeholder='Identificador do paciente'
            value={filters.patientId}
            onChange={e => onFilterChange('patientId', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SelectBox
            label='Status da Revisão'
            menuItems={STATUS_OPTIONS}
            control={[filters.status, value => onFilterChange('status', value)]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ position: 'relative' }}>
                <DatePicker
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      backgroundColor: 'background.paper'
                    }
                  }}
                  label='Data do exame de'
                  value={filters.examDateStart}
                  onChange={value => onFilterChange('examDateStart', value)}
                  maxDate={dayjs()}
                />
                {filters.examDateStart && (
                  <IconButton
                    size='small'
                    onClick={() => onFilterChange('examDateStart', null)}
                    title='Limpar data'
                    sx={{
                      position: 'absolute',
                      right: 40,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 1
                    }}
                  >
                    <Clear fontSize='small' />
                  </IconButton>
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ position: 'relative' }}>
                <DatePicker
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      backgroundColor: 'background.paper'
                    }
                  }}
                  label='Data do exame até'
                  value={filters.examDateEnd}
                  onChange={value => onFilterChange('examDateEnd', value)}
                  maxDate={dayjs()}
                  minDate={filters.examDateStart || undefined}
                />
                {filters.examDateEnd && (
                  <IconButton
                    size='small'
                    onClick={() => onFilterChange('examDateEnd', null)}
                    title='Limpar data'
                    sx={{
                      position: 'absolute',
                      right: 40,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 1
                    }}
                  >
                    <Clear fontSize='small' />
                  </IconButton>
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          size={{ xs: 12 }}
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}
        >
          <ButtonBase onClick={onClearFilters} variant='outlined'>
            Limpar Filtros
          </ButtonBase>
          <ButtonBase onClick={onApplyFilters}>Aplicar Filtros</ButtonBase>
        </Grid>
      </Grid>
    </>
  )
}

export default DiagnosticsFilters
